<template>
  <v-dialog :value="value" max-width="1100" scrollable @input="(v) => $emit('input', v)">
    <v-card v-if="artwork" class="cad" :class="{ 'cad--view': !canUpscale }">
      <div class="cad__head">
        <v-icon small class="mr-2">mdi-palette-outline</v-icon>
        <b>{{ artwork.styleId || "Artwork" }}</b>
        <span v-if="artwork.ordered" class="cad__badge cad__badge--ordered">bestellt</span>
        <span class="cad__muted ml-2">{{ createdLabel }}</span>
        <v-spacer />
        <a :href="artwork.url" target="_blank" rel="noopener" class="cad__iconBtn" title="In neuem Tab öffnen"><v-icon small>mdi-open-in-new</v-icon></a>
        <button type="button" class="cad__iconBtn" title="Schließen" @click="$emit('input', false)"><v-icon small>mdi-close</v-icon></button>
      </div>

      <div class="cad__body">
        <div class="cad__image"><img :src="artwork.url" alt="" /></div>

        <div v-if="canUpscale" class="cad__side">
          <!-- Originalfotos: nachvollziehbar → automatisch gewählt, sonst alle Fotos des Kunden zur Auswahl -->
          <div class="cad__section">
            <div class="cad__label">
              Originalfotos
              <span v-if="tracedUrls.length" class="cad__muted">{{ tracedUrls.length }} sind in dieses Artwork eingeflossen und automatisch gewählt</span>
              <span v-else class="cad__muted">nicht nachvollziehbar, bitte die passenden wählen (max. 4)</span>
            </div>
            <div v-if="photoOptions.length" class="cad__photos">
              <button
                v-for="p in photoOptions"
                :key="p.url"
                type="button"
                class="cad__photo"
                :class="{ 'cad__photo--on': selected.includes(p.url) }"
                :title="p.traced ? 'In dieses Artwork eingeflossen' : 'Foto aus einem anderen Artwork des Kunden'"
                @click="togglePhoto(p.url)"
              >
                <img :src="p.url" alt="" loading="lazy" />
                <span v-if="selected.includes(p.url)" class="cad__check"><v-icon x-small color="white">mdi-check</v-icon></span>
                <span v-if="p.traced" class="cad__traced">eingeflossen</span>
              </button>
            </div>
            <div v-else class="cad__muted">Keine Originalfotos mehr vorhanden. Der Upscale läuft dann ohne Prüfung und Detail-Pass.</div>
          </div>

          <!-- Druckprofil: wie bestellt, sonst höchste Version des Stils -->
          <div class="cad__section">
            <div class="cad__label">Druckprofil</div>
            <v-select v-model="profileKey" :items="profileItems" dense outlined hide-details :loading="metaLoading" label="Stil-Version" />
            <div v-if="metaError" class="cad__error mt-1">{{ metaError }}</div>
            <div v-if="profile" class="cad__chips">
              <span class="cad__chip">Upscale {{ profile.print.upscale && profile.print.upscale.engine }}</span>
              <span v-if="selected.length" class="cad__chip">Prüfung</span>
              <span v-if="selected.length && profile.print.detailPass && profile.print.detailPass.enabled" class="cad__chip">Detail-Pass</span>
              <span v-if="profile.transparent" class="cad__chip">Freistellen {{ (profile.print.cutout && profile.print.cutout.method) || "dual" }}</span>
            </div>
          </div>

          <div class="cad__section cad__row">
            <div class="cad__label mb-0">Auflösung</div>
            <div class="cad__seg">
              <button v-for="r in ['2K', '4K']" :key="r" type="button" :class="{ on: resolution === r }" @click="resolution = r">{{ r }}</button>
            </div>
          </div>

          <v-btn color="primary" depressed rounded block :disabled="!canStart" :loading="starting" @click="start">
            <v-icon left small>mdi-arrow-expand-all</v-icon>Upscale-Job starten
          </v-btn>
          <div v-if="error" class="cad__error mt-2">{{ error }}</div>
          <div class="cad__muted mt-2">Lab-Job wie im Upscale-Tab: kein Task, kein Auto-Import. Ergebnis liegt einen Tag im Speicher, bei Bedarf herunterladen.</div>

          <div v-for="jobId in jobIds" :key="jobId" class="cad__run">
            <UpscaleProgress :job-id="jobId" />
            <UpscaleJobResult :job-id="jobId" />
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
// Bestelldetails → „Alle Artworks des Kunden" → Klick (Malte 19.10.): Artwork groß + Upscale-Job starten.
// Originalfotos aus artworks_generated.inputStoragePaths (listUserArtworks → photoUrls), Druckprofil wie bestellt
// (versionMeta des Tasks) oder höchste Version des Stils (getStyleMeta). Start über startLabUpscale (Quelle 'lab').
import dayjs from "dayjs";
import { httpsCallable } from "firebase/functions";
import { functions as mainFunctions } from "../../../../fb";
import UpscaleProgress from "@/components/Upscales/UpscaleProgress.vue";
import UpscaleJobResult from "@/components/Upscales/UpscaleJobResult";
import { trackUpscaleJob } from "@/generateRuns";

// gleicher Schlüssel wie im Upscale-Tab (UpscaleLabPanel.vue) → Läufe erscheinen auch dort
const RUNS_KEY = "taskdash_upscale_lab_runs_v1";
const MAX_PHOTOS = 4;

let styleMetaCache = null;

/** Storage-Pfad aus einer Firebase-Download-URL (…/o/artworks%2Fxyz.png?…) */
function storagePathOf(url) {
  const m = String(url || "").match(/\/o\/([^?]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

export default {
  name: "CustomerArtworkDialog",
  components: { UpscaleProgress, UpscaleJobResult },
  props: {
    value: Boolean,
    artwork: { type: Object, default: null },
    /** alle Artworks des Kunden (für die Foto-Auswahl, wenn nichts nachvollziehbar ist) */
    artworks: { type: Array, default: () => [] },
    /** Druckprofile der bestellten Tasks: [{ paths, version, print, transparent, photos }] */
    orderProfiles: { type: Array, default: () => [] },
    orderName: { type: String, default: "" },
    /** startLabUpscale erlaubt nur ADMIN/MANAGER — sonst nur Ansicht */
    canUpscale: { type: Boolean, default: false },
  },
  data() {
    return {
      styles: styleMetaCache || [],
      metaLoading: false,
      metaError: "",
      profileKey: "",
      selected: [],
      resolution: "4K",
      starting: false,
      error: "",
      jobIds: [],
    };
  },
  computed: {
    createdLabel() {
      return this.artwork && this.artwork.createdAt ? dayjs(this.artwork.createdAt).format("DD.MM.YYYY HH:mm") : "";
    },
    /** Bestellter Task zu genau diesem Artwork (gleicher Storage-Pfad) */
    orderedProfile() {
      const path = storagePathOf(this.artwork && this.artwork.url);
      return (path && this.orderProfiles.find((p) => p.paths.includes(path))) || null;
    },
    /** Fotos, die nachweislich in dieses Artwork eingeflossen sind */
    tracedUrls() {
      const a = this.artwork || {};
      const own = Array.isArray(a.photoUrls) && a.photoUrls.length ? a.photoUrls : a.photoUrl ? [a.photoUrl] : [];
      if (own.length) return own;
      return (this.orderedProfile && this.orderedProfile.photos) || [];
    },
    photoOptions() {
      const out = [];
      const seen = new Set();
      const add = (url, traced) => {
        const key = storagePathOf(url) || url;
        if (!url || seen.has(key)) return;
        seen.add(key);
        out.push({ url, traced });
      };
      this.tracedUrls.forEach((u) => add(u, true));
      // Nicht nachvollziehbar: alle Originale des Kunden (andere Artworks + bestellte Tasks) zur Auswahl
      if (!this.tracedUrls.length) {
        for (const a of this.artworks) (Array.isArray(a.photoUrls) && a.photoUrls.length ? a.photoUrls : [a.photoUrl]).forEach((u) => add(u, false));
        for (const p of this.orderProfiles) (p.photos || []).forEach((u) => add(u, false));
      }
      return out;
    },
    profiles() {
      const out = [];
      const op = this.orderedProfile;
      if (op && op.print) out.push({ key: "order", label: `Wie bestellt · ${this.artwork.styleId || "?"} v${op.version || "?"}`, print: op.print, transparent: !!op.transparent, styleId: this.artwork.styleId, version: op.version });
      const style = this.styles.find((s) => s.id === (this.artwork && this.artwork.styleId));
      for (const v of (style && style.perVersion) || []) {
        if (v.print) out.push({ key: `${style.id}|${v.version}`, label: `${style.id} v${v.version}${v.transparent ? " · Freisteller" : ""}`, print: v.print, transparent: !!v.transparent, styleId: style.id, version: v.version });
      }
      return out;
    },
    profileItems() {
      return this.profiles.map((p) => ({ text: p.label, value: p.key }));
    },
    profile() {
      return this.profiles.find((p) => p.key === this.profileKey) || null;
    },
    canStart() {
      return !!this.artwork && !!this.artwork.url && !!this.profile && !this.starting;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(open) {
        if (!open) return;
        this.error = "";
        this.jobIds = [];
        this.selected = this.tracedUrls.slice(0, MAX_PHOTOS);
        this.profileKey = "";
        this.pickDefaultProfile();
        if (!this.styles.length) this.loadMeta();
      },
    },
    profiles() {
      if (!this.profile) this.pickDefaultProfile();
    },
  },
  methods: {
    async loadMeta() {
      this.metaLoading = true;
      this.metaError = "";
      try {
        const call = httpsCallable(mainFunctions, "themePreviewAction", { timeout: 60000 });
        const { data } = await call({ action: "styleMeta", payload: {} });
        styleMetaCache = (data && data.styles) || [];
        this.styles = styleMetaCache;
      } catch (e) {
        this.metaError = "Stil-Daten nicht erreichbar: " + (e.message || e);
      } finally {
        this.metaLoading = false;
      }
    },
    /** Standard: wie bestellt, sonst höchste Version des Stils */
    pickDefaultProfile() {
      if (!this.profiles.length) return;
      const order = this.profiles.find((p) => p.key === "order");
      const highest = this.profiles.filter((p) => p.key !== "order").sort((a, b) => Number(b.version) - Number(a.version))[0];
      this.profileKey = (order || highest || this.profiles[0]).key;
    },
    togglePhoto(url) {
      if (this.selected.includes(url)) this.selected = this.selected.filter((u) => u !== url);
      else if (this.selected.length < MAX_PHOTOS) this.selected = [...this.selected, url];
    },
    async start() {
      if (!this.canStart) return;
      this.starting = true;
      this.error = "";
      try {
        const p = this.profile;
        const a = this.artwork;
        // Die Kette arbeitet auf dem opaken Bild (V9-Freisteller: opaqueUrl), Freistellen macht das Profil
        const source = a.opaqueUrl || a.url;
        const photos = this.selected.slice(0, MAX_PHOTOS);
        const detail = !!(photos.length && p.print.detailPass && p.print.detailPass.enabled);
        const label = `${this.orderName ? this.orderName + " · " : ""}${p.styleId} v${p.version}`;
        const call = httpsCallable(mainFunctions, "startLabUpscale", { timeout: 60000 });
        const { data } = await call({
          print: p.print,
          transparent: p.transparent,
          styleId: p.styleId,
          version: p.version,
          artworkUrl: source,
          opaqueUrl: source,
          photos,
          resolution: this.resolution,
          score: photos.length > 0,
          detailPass: detail,
          cutout: p.transparent,
          label: `TaskDash Kunden-Artwork ${label}`,
        });
        this.jobIds = [data.jobId, ...this.jobIds];
        trackUpscaleJob(data.jobId, `Upscale ${label}`);
        const stepsLabel = [`Upscale ${this.resolution}`, photos.length && "Prüfung", detail && "Detail-Pass", p.transparent && "Freisteller"].filter(Boolean);
        let runs = [];
        try {
          runs = JSON.parse(localStorage.getItem(RUNS_KEY) || "[]");
        } catch (e) {
          runs = [];
        }
        localStorage.setItem(RUNS_KEY, JSON.stringify([{ jobId: data.jobId, label, stepsLabel, at: Date.now() }, ...(Array.isArray(runs) ? runs : [])].slice(0, 20)));
      } catch (e) {
        this.error = e.message || String(e);
      } finally {
        this.starting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.cad {
  border-radius: 16px !important;
  font-size: 13px;
  &__head { display: flex; align-items: center; padding: 12px 16px; border-bottom: 1px solid rgba(0, 0, 0, 0.06); }
  &--view &__body { grid-template-columns: 1fr; }
  &__body { display: grid; grid-template-columns: minmax(0, 1fr) 380px; gap: 16px; padding: 16px; overflow-y: auto;
    @media (max-width: 900px) { grid-template-columns: 1fr; }
  }
  &__image { background: #f4f4f6; border-radius: 12px; display: flex; align-items: center; justify-content: center; min-height: 300px;
    img { max-width: 100%; max-height: 70vh; border-radius: 8px; }
  }
  &__side { display: grid; gap: 12px; align-content: start; }
  &__section { display: grid; gap: 6px; }
  &__row { display: flex; align-items: center; justify-content: space-between; }
  &__label { font-weight: 700; display: grid; gap: 2px; }
  &__muted { color: rgba(0, 0, 0, 0.5); font-size: 12px; font-weight: 400; }
  &__error { color: #c62828; font-size: 12px; font-weight: 600; }
  &__badge { margin-left: 8px; padding: 1px 8px; border-radius: 999px; font-size: 11px; font-weight: 700; background: #eef0f3;
    &--ordered { background: #4c9a5b; color: #fff; }
  }
  &__iconBtn { border: 0; background: none; cursor: pointer; padding: 4px; border-radius: 50%; text-decoration: none;
    &:hover { background: rgba(0, 0, 0, 0.06); }
  }
  &__photos { display: flex; flex-wrap: wrap; gap: 8px; }
  &__photo { position: relative; width: 78px; height: 78px; padding: 0; border-radius: 10px; overflow: hidden; border: 2px solid transparent; background: #eee; cursor: pointer; opacity: 0.6;
    img { width: 100%; height: 100%; object-fit: cover; display: block; }
    &--on { border-color: #282828; opacity: 1; }
  }
  &__check { position: absolute; top: 4px; right: 4px; width: 18px; height: 18px; border-radius: 50%; background: #282828; display: flex; align-items: center; justify-content: center; }
  &__traced { position: absolute; left: 0; right: 0; bottom: 0; padding: 1px 0; font-size: 9.5px; font-weight: 700; text-align: center; color: #fff; background: rgba(76, 154, 91, 0.9); }
  &__chips { display: flex; flex-wrap: wrap; gap: 6px; }
  &__chip { background: #f1f2f6; border-radius: 999px; padding: 2px 9px; font-size: 11.5px; }
  &__seg { display: inline-flex; background: #f1f2f6; border-radius: 999px; padding: 2px;
    button { border: 0; background: none; padding: 4px 12px; border-radius: 999px; font-size: 12px; font-weight: 600; color: rgba(0, 0, 0, 0.55); cursor: pointer; }
    button.on { background: #fff; color: #282828; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12); }
  }
  &__run { border-top: 1px solid rgba(0, 0, 0, 0.06); padding-top: 10px; }
}
</style>
